import { MIP_INVESTOR_INVITATION_FORM } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/constants';
import { ContentView } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendInvitationModal/ContentView';
import { ConfirmationModalV2 } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModalV2';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { em } from '@mantine/core';
import {
	GetInvestorDataForSendModal,
	SendInvestorInviteDto,
} from '@dealstack/shared/lib/generated';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import styled from 'styled-components';

interface Props {
	className?: string;
	onClose: () => void;
	sendInvitation: (invites: SendInvestorInviteDto) => Promise<void>;
	isLoading: boolean;
	investors: GetInvestorDataForSendModal[];
}

interface InvestorInvite {
	investorId: string;
	shouldSend: boolean;
}

interface SendInvitesPayload {
	invites: InvestorInvite[];
	customizedEmailBody: string;
}

const SendInvitationModal = ({
	sendInvitation,
	isLoading,
	onClose,
	className,
	investors,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const defaultTemplate = tMipManage('invitation_modal.email_by_default');
	const investorInvitationForm = useForm<{ custom_text_field: string }>({
		mode: 'all',
		defaultValues: {
			[MIP_INVESTOR_INVITATION_FORM.CUSTOM_INVITATION_TEXT_AREA]: defaultTemplate,
		},
	});

	const customizedEmailBody = investorInvitationForm.watch(
		`${MIP_INVESTOR_INVITATION_FORM.CUSTOM_INVITATION_TEXT_AREA}`
	);

	const investorsWithDetails = investors.map((investor) => ({
		investorId: investor.id,
		shouldSend: false,
	}));

	const [isSuccess, setIsSuccess] = useState<boolean>(false);
	const [investorsReadyToInvite, setInvestorsReadyToInvite] =
		useState<InvestorInvite[]>(investorsWithDetails);
	const [isSendInvitationButtonDisabled, setSendInvitationButtonDisabled] = useState(false);

	const onClickDone = () => {
		setIsSuccess(true);
	};

	const onClickInviteInvestor = (investorId: string) => {
		setInvestorsReadyToInvite((investors: InvestorInvite[]) =>
			investors.map((invite) =>
				invite.investorId === investorId ? { ...invite, shouldSend: true } : invite
			)
		);
	};

	const onClickInvestorsRemaining = () => {
		setInvestorsReadyToInvite((investors: InvestorInvite[]) =>
			investors.map((invite) =>
				!invite.shouldSend ? { ...invite, shouldSend: true } : invite
			)
		);
		setIsSuccess(true);
	};

	const onSendInvestorInvitation = async (payload: SendInvitesPayload) => {
		await sendInvitation(payload);
		onClose();
	};

	const showContentView = () => {
		setIsSuccess(false);
		setInvestorsReadyToInvite(investorsWithDetails);
	};

	const investorsInvited = investorsReadyToInvite
		.filter((investor) => investor.shouldSend)
		.map((investor) => investor.investorId);

	const triggerOnSendInvitation = async () => {
		setSendInvitationButtonDisabled(true);

		const payload: SendInvitesPayload = {
			customizedEmailBody,
			invites: investorsReadyToInvite.filter((invite) => invite.shouldSend),
		};

		await onSendInvestorInvitation(payload);
	};
	const totalInvestorNumber = investorsInvited.length.toString();

	const titleTranslationKey =
		investorsInvited.length > 1
			? 'invitation_modal.confirmation_alert_title'
			: 'invitation_modal.confirmation_alert_title_for_one_investor';

	const descriptionTranslationKey =
		investorsInvited.length > 1
			? 'invitation_modal.confirmation_alert_description'
			: 'invitation_modal.confirmation_alert_description_for_one_investor';

	const confirmationInvitationTitleText = tMipManage(titleTranslationKey, {
		totalInvestorNumber,
	});

	const confirmationInvitationDescriptionText = tMipManage(descriptionTranslationKey, {
		totalInvestorNumber,
	});

	return (
		<ReactModal className={className} contentLabel="send-invitation-v2-modal" isOpen>
			{isLoading && <InlineLoader />}
			{!isLoading && isSuccess && (
				<ConfirmationModalV2
					onCancel={showContentView}
					onSubmit={triggerOnSendInvitation}
					title={confirmationInvitationTitleText}
					description={confirmationInvitationDescriptionText}
					disabledSubmitButton={isSendInvitationButtonDisabled}
				/>
			)}
			{!isLoading && !isSuccess && (
				<ContentView
					onClose={onClose}
					investors={investors}
					invitationForm={investorInvitationForm}
					onClickDone={onClickDone}
					onClickInvestorsRemaining={onClickInvestorsRemaining}
					investorsInvited={investorsInvited}
					onClickInviteInvestor={onClickInviteInvestor}
				/>
			)}
		</ReactModal>
	);
};

const Styled = styled(SendInvitationModal)`
	display: flex;
	flex-direction: column;
	width: 48rem;
	background-color: ${({ theme }) => theme.v2.color.white[500]};
	border-radius: ${em(8)};
`;

export { Styled as SendInvitationModal };
